/**
 * PageEditor — per-page captions and annotations for the Case Report Builder.
 *
 * The document is DERIVED on every input change and never stored, so everything
 * this panel collects lives in an `edits` map keyed by `editKeyFor(page)` — a
 * content-derived key (the PageNN number, or the recipe id), never `page.id`,
 * which is positional. See edits.js for why that distinction is the whole design.
 *
 * A page with no stable key cannot be edited and says so, rather than accepting
 * edits that would silently reattach themselves to a different page after the
 * next redraft.
 */
import { useRef, useState } from 'react'
import {
  CAPTION_SLOTS, ANNOTATION_KINDS, editKeyFor, makeAnnotation, newEdit, annotationTargets,
} from '../../lib/caseReport/edits.js'

const CAPTION_LABEL = {
  tl: 'Top left', tc: 'Top centre', tr: 'Top right',
  bl: 'Bottom left', bc: 'Bottom centre', br: 'Bottom right',
}

/** [edit field, label, the page block it belongs to, one line of guidance] */
const PAGE_FIELD_UI = [
  ['summary', 'Case summary', 'info', 'Printed under the scan data, in the text column. What was planned, in the planner’s words.'],
  ['notes', 'Notes', 'measure', 'Printed at the foot of the page, under “Notes:”. Where each measurement was taken from.'],
]

/** Fields each annotation kind needs, so the form only asks for what it uses. */
const GEOMETRY = {
  text: ['x', 'y'],
  arrow: ['x1', 'y1', 'x2', 'y2'],
  dimension: ['x1', 'y1', 'x2', 'y2'],
  leader: ['x1', 'y1', 'x2', 'y2'],
  line: ['x1', 'y1', 'x2', 'y2'],
  // ONE radius — rx/ry normalise against width and height separately, so equal
  // values draw an oval rather than a circle.
  circle: ['cx', 'cy', 'r'],
}
const HAS_TEXT = new Set(['text', 'arrow', 'dimension'])

const input = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs'
const btn = 'rounded-md px-2 py-1 text-xs font-medium'

const pageLabel = (p, i) =>
  `${i + 1}. ${p.captions?.tc || p.captions?.tl || (p.block === 'text' ? 'Text page' : p.block)}`

export default function PageEditor({ doc, edits, setEdits, orphans = [], allowOmit = true }) {
  const [sel, setSel] = useState(0)
  const [kind, setKind] = useState('arrow')
  const [fields, setFields] = useState({ text: '' })
  const [target, setTarget] = useState('')
  // Monotonic, so two annotations added inside the same millisecond cannot share
  // an id — which would collide as a React key AND make remove-by-id delete the
  // wrong one.
  const seq = useRef(0)

  const page = doc.pages[sel]
  const key = page ? editKeyFor(page) : null
  const edit = (key && edits[key]) || newEdit()

  const patch = (fn) => {
    if (!key) return
    setEdits((prev) => {
      const next = { ...prev }
      const cur = { ...newEdit(), ...next[key] }
      fn(cur)
      // Drop an edit that no longer says anything, so the stamp does not change
      // and a stray empty record cannot look like an unsaved change.
      //
      // `steps` MUST be counted here. Left out, an edit whose only content is a
      // step narrative reads as empty and is deleted on the same keystroke that
      // created it — the operator types and nothing is ever saved.
      const empty = !Object.keys(cur.captions).length && !cur.annotations.length
        && !Object.keys(cur.steps ?? {}).length && !Object.keys(cur.fields ?? {}).length && !cur.hidden
      if (empty) delete next[key]
      else next[key] = cur
      return next
    })
  }

  const setCaption = (slot, value) =>
    patch((c) => { c.captions = { ...c.captions, [slot]: value } })

  const resetCaption = (slot) =>
    patch((c) => { const n = { ...c.captions }; delete n[slot]; c.captions = n })

  const setField = (name, value) =>
    patch((c) => { c.fields = { ...(c.fields ?? {}), [name]: value } })

  const resetField = (name) =>
    patch((c) => { const n = { ...(c.fields ?? {}) }; delete n[name]; c.fields = n })

  const setStepText = (i, value) =>
    patch((c) => { c.steps = { ...c.steps, [String(i)]: value } })

  const resetStepText = (i) =>
    patch((c) => { const n = { ...c.steps }; delete n[String(i)]; c.steps = n })

  const addAnnotation = () => {
    let a
    try {
      seq.current += 1
      const [tKind, tIndex] = target ? target.split(':') : []
      a = makeAnnotation(`e${seq.current}-${kind}`, kind, {
        ...fields,
        target: tKind ? { kind: tKind, index: +tIndex } : null,
      })
    } catch (e) {
      // Surfaced rather than swallowed: an annotation the writer cannot draw
      // would simply not appear on the page, with no error anywhere.
      window.alert(e.message)
      return
    }
    patch((c) => { c.annotations = [...c.annotations, a] })
    setFields({ text: '' })
  }

  const removeAnnotation = (id) =>
    patch((c) => { c.annotations = c.annotations.filter((a) => a.id !== id) })

  const editedKeys = new Set(Object.keys(edits))

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-4">
      <div>
        <span className="text-xs font-semibold text-gray-700 block mb-1">Pages</span>
        <ol className="max-h-72 overflow-auto rounded-md border border-gray-200 divide-y">
          {doc.pages.map((p, i) => {
            const k = editKeyFor(p)
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setSel(i)}
                  className={`w-full text-left px-2 py-1 text-xs ${i === sel ? 'bg-brand-50 font-medium' : ''}`}
                >
                  <span className={edits[k]?.hidden ? 'line-through text-gray-400' : ''}>
                    {pageLabel(p, i)}
                  </span>
                  {k && editedKeys.has(k) && <span className="ml-1 text-brand-600">•</span>}
                  {!k && <span className="ml-1 text-gray-300" title="not editable">—</span>}
                </button>
              </li>
            )
          })}
        </ol>
        {!!orphans.length && (
          <p className="mt-2 text-xs text-amber-700">
            {orphans.length} edit{orphans.length === 1 ? '' : 's'} no longer match any page
            ({orphans.join(', ')}). They are kept, not deleted — restore the file or clear them.
          </p>
        )}
      </div>

      <div>
        {!page && <p className="text-xs text-gray-500">No page selected.</p>}

        {page && !key && (
          <p className="text-xs text-gray-600">
            This page has no stable identity across redrafts, so it cannot carry edits.
            Anything typed here would reattach itself to a different page the next time
            the draft is rebuilt.
          </p>
        )}

        {page && key && (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                key <code className="text-gray-700">{key}</code>
              </span>
              {/* Only where there is no page list (a folder named by page). With a
                  page list, "Remove" there is the one way to drop a page. */}
              {allowOmit && (
                <label className="flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!edit.hidden}
                    onChange={(e) => patch((c) => { c.hidden = e.target.checked })}
                  />
                  Omit this page
                </label>
              )}
            </div>

            <span className="text-xs font-semibold text-gray-700 block mb-1">Captions</span>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {CAPTION_SLOTS.map((slot) => {
                const overridden = Object.hasOwn(edit.captions, slot)
                const value = overridden ? edit.captions[slot] : (page.captions?.[slot] ?? '')
                return (
                  <label key={slot} className="block">
                    <span className="text-[11px] text-gray-500 flex items-center justify-between">
                      {CAPTION_LABEL[slot]}
                      {overridden && (
                        <button type="button" onClick={() => resetCaption(slot)}
                          className="text-brand-600 underline">reset</button>
                      )}
                    </span>
                    <input
                      className={`${input} ${overridden ? 'border-brand-400' : ''}`}
                      value={value}
                      placeholder="—"
                      onChange={(e) => setCaption(slot, e.target.value)}
                    />
                  </label>
                )
              })}
            </div>

            {/* The page's own typed text. Source data carries the case summary
                (FIBULA p2 — "The left fibula has been utilised…"); a measurements
                page carries its notes (ORTHO p8 — where the numbers were taken).
                Both are the planner's sentences, so they are typed here, not drafted. */}
            {PAGE_FIELD_UI.filter(([, , block]) => page.block === block).map(([name, label, , hint]) => {
              const overridden = Object.hasOwn(edit.fields ?? {}, name)
              const value = overridden ? edit.fields[name] : (page[name] ?? '')
              return (
                <label key={name} className="block mb-3">
                  <span className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                    {label}
                    {overridden && (
                      <button type="button" onClick={() => resetField(name)}
                        className="text-brand-600 underline font-normal text-[11px]">reset</button>
                    )}
                  </span>
                  <p className="text-[11px] text-gray-500 mb-1">{hint}</p>
                  <textarea
                    rows={3}
                    className={`${input} ${overridden ? 'border-brand-400' : ''}`}
                    value={value}
                    data-testid={`page-field-${name}`}
                    onChange={(e) => setField(name, e.target.value)}
                  />
                </label>
              )
            })}

            {/* Steps pages only (reference pp. 5-6). The draft builds the panels and
                leaves every narrative blank, because what each step says — "moved to
                the patient's left by 3.85mm" — is the planner's decision and is not
                readable off a mesh. Without this box those pages cannot be finished
                in the tool at all. */}
            {Array.isArray(page.steps) && page.steps.length > 0 && (
              <>
                <span className="text-xs font-semibold text-gray-700 block mb-1">Step narrative</span>
                <p className="text-[11px] text-gray-500 mb-2">
                  Printed under each panel after its bold “{page.steps[0]?.label || 'Step 01'}:” label.
                </p>
                <div className="space-y-2 mb-3">
                  {page.steps.map((st, i) => {
                    const overridden = Object.hasOwn(edit.steps ?? {}, String(i))
                    const value = overridden ? edit.steps[String(i)] : (st.text ?? '')
                    return (
                      <label key={st.label ?? i} className="block">
                        <span className="text-[11px] text-gray-500 flex items-center justify-between">
                          {st.label || `Step ${i + 1}`}{st.view ? ` (${st.view})` : ''}
                          {overridden && (
                            <button type="button" onClick={() => resetStepText(i)}
                              className="text-brand-600 underline">reset</button>
                          )}
                        </span>
                        <textarea
                          rows={2}
                          className={`${input} ${overridden ? 'border-brand-400' : ''}`}
                          value={value}
                          placeholder="What was done at this step"
                          onChange={(e) => setStepText(i, e.target.value)}
                        />
                      </label>
                    )
                  })}
                </div>
              </>
            )}

            <span className="text-xs font-semibold text-gray-700 block mb-1">
              Annotations — marking &amp; measuring
            </span>
            <p className="text-[11px] text-gray-500 mb-2">
              Positions are fractions of whatever you attach to, 0–1: x from its left
              edge, y from its top. Attach to a panel and 0.5/0.5 is that panel’s centre;
              attach to the whole page and it is the centre of the sheet. An arrow or
              dimension carries its value as text (e.g. “3.85mm”).
            </p>

            {!!edit.annotations.length && (
              <ul className="mb-2 divide-y rounded-md border border-gray-200">
                {edit.annotations.map((a) => (
                  <li key={a.id} className="flex items-center justify-between px-2 py-1 text-xs">
                    <span>
                      <span className="font-medium">{a.kind}</span>
                      {a.text ? ` — “${a.text}”` : ''}
                      <span className="text-gray-400">
                        {a.target ? ` · ${a.target.kind} ${a.target.index + 1}` : ' · whole page'}
                      </span>
                    </span>
                    <button type="button" onClick={() => removeAnnotation(a.id)}
                      className={`${btn} text-red-600`}>remove</button>
                  </li>
                ))}
              </ul>
            )}

            <div className="rounded-md border border-gray-200 p-2">
              <div className="flex flex-wrap items-end gap-2">
                <label className="block">
                  <span className="text-[11px] text-gray-500">Attach to</span>
                  <select className={input} value={target} onChange={(e) => setTarget(e.target.value)}>
                    {annotationTargets(page).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] text-gray-500">Kind</span>
                  <select className={input} value={kind} onChange={(e) => setKind(e.target.value)}>
                    {ANNOTATION_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
                {HAS_TEXT.has(kind) && (
                  <label className="block flex-1 min-w-[8rem]">
                    <span className="text-[11px] text-gray-500">Text / value</span>
                    <input className={input} value={fields.text ?? ''}
                      onChange={(e) => setFields((f) => ({ ...f, text: e.target.value }))} />
                  </label>
                )}
                {GEOMETRY[kind].map((g) => (
                  <label key={g} className="block w-16">
                    <span className="text-[11px] text-gray-500">{g}</span>
                    <input className={input} type="number" step="0.01" min="0" max="1"
                      value={fields[g] ?? ''}
                      onChange={(e) => setFields((f) => ({ ...f, [g]: e.target.value }))} />
                  </label>
                ))}
                <button type="button" onClick={addAnnotation}
                  className={`${btn} bg-brand-600 text-white`}>Add</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
